// items-chemistry.js — Probe item bank: CHEMISTRY (calibration-adjacent domain)
// =============================================================================
// One probe per chemistry node, keyed to the REAL registry node ids in
// graph-data.js. Chemistry is a clean linear CHAIN hanging off physics:
//   quantum_mechanics(soft) -> atomic_theory -> chemical_bonding
//                            -> organic_chemistry -> biochemistry
// This chain topology is deliberately the same favourable shape as the math
// spine, so it also serves as a test of deep-chain up-propagation across a
// domain seam (physics -> chemistry): one deep correct answer should lift the
// whole chemistry ancestry, and one deep miss should cap the chain below it.
//
// AUTHORING STANDARD (identical to items-math.js / items-physics.js):
//   1. DIAGNOSTIC, not trivia. Correct answer requires grasping the node's core
//      idea, not recalling a definition or a name.
//   2. ONE concept. Each probe targets the single most load-bearing idea, so a
//      miss cleanly implicates THAT node (and its DAG descendants).
//   3. PREREQUISITE-CLEAN. Answering must not secretly require a HARDER topic
//      in the chain (an atomic_theory probe must not need bonding; a bonding
//      probe must not need organic reaction mechanisms), NOR require the math/
//      physics prereq machinery. We probe the CONCEPT the node adds.
//   4. DISTRACTORS ARE DIAGNOSTIC. Wrong options encode the specific canonical
//      misconception for that topic (noted per item in `trap`).
//   5. STABLE & UNAMBIGUOUS. Exactly one defensible answer.
//
// Schema per item (merged onto the registry node at load time):
//   { q, opts:[4], a:index, concept, trap }  — a = index of the correct option.
// =============================================================================

export const CHEMISTRY_ITEMS = {

  // --- atomic_theory (prereq: quantum_mechanics soft) ------------------------
  // Load-bearing idea: chemical behaviour is governed by VALENCE (outer-shell)
  // electrons, and the periodic table's structure reflects shell filling.
  // Probe-clean: needs no bonding theory, just why the table is periodic.
  atomic_theory: {
    q: 'Why do elements in the same column of the periodic table (e.g. Li, Na, K) behave chemically alike?',
    opts: [
      'They have the same number of electrons in their outer shell',
      'They have the same total number of electrons',
      'They have the same number of neutrons in the nucleus',
      'They have identical atomic masses'
    ],
    a: 0,
    concept: 'Chemical behaviour is set by the number of valence (outer-shell) electrons; a column shares a valence count, which is why the table is periodic. This is the single idea that turns "a list of elements" into a predictive structure.',
    trap: 'Picking "same total electrons" or "same mass" reveals the learner sees the table as an ordering by size rather than a map of electron configuration \u2014 missing the entire organizing principle of atomic theory. A miss here should cap belief in all of chemistry below it.'
  },

  // --- chemical_bonding (prereq: atomic_theory hard) -------------------------
  // Load-bearing idea: bonding type is about what atoms DO with valence
  // electrons -- transfer (ionic) vs share (covalent) -- driven by
  // electronegativity difference. Probe-clean: needs valence concept only.
  chemical_bonding: {
    q: 'What most fundamentally distinguishes an ionic bond from a covalent bond?',
    opts: [
      'Ionic bonds share electrons; covalent bonds transfer them',
      'Ionic bonds transfer electrons; covalent bonds share them',
      'Ionic bonds occur only in metals; covalent only in gases',
      'Ionic bonds are always stronger than covalent bonds'
    ],
    a: 1,
    concept: 'Ionic bonding = electron transfer (creating charged ions that attract); covalent = electron sharing. The distinction follows from electronegativity difference and is the foundation for predicting molecular structure.',
    trap: 'Option 0 is the reversed definition \u2014 the single most common bonding error, revealing the terms were memorized without the transfer-vs-share mechanism. Option 3 ("ionic always stronger") is a persistent strength misconception. Both mean the learner cannot yet predict how atoms combine.'
  },

  // --- organic_chemistry (prereq: chemical_bonding hard) ---------------------
  // Load-bearing idea: carbon's tetravalence (4 bonds) is what enables the
  // vast diversity of organic molecules -- chains, rings, branching.
  // Probe-clean: needs bonding (shared electrons) but no reaction mechanisms.
  organic_chemistry: {
    q: 'Why can carbon form such an enormous variety of different molecules?',
    opts: [
      'Carbon forms four covalent bonds, allowing long chains, rings, and branches',
      'Carbon is the most abundant element on Earth',
      'Carbon bonds are ionic, so it attracts many different atoms',
      'Carbon has the lowest atomic mass among the nonmetals'
    ],
    a: 0,
    concept: 'Carbon\u2019s tetravalence (four covalent bonds) plus its ability to bond to itself lets it build chains, rings, and branched skeletons of essentially unlimited complexity \u2014 the structural basis of all organic chemistry and the molecules of life.',
    trap: 'Option 1 (abundance) confuses availability with bonding capacity; option 2 mislabels carbon bonds as ionic (contradicting the bonding prereq). Both reveal the learner hasn\u2019t connected carbon\u2019s four-bond structure to molecular diversity \u2014 the load-bearing idea of the whole subject.'
  },

  // --- biochemistry (prereq: organic_chemistry hard; hub=1) ------------------
  // Load-bearing idea: enzymes are catalysts -- they lower activation energy to
  // speed reactions WITHOUT being consumed or changing the reaction's
  // equilibrium. Probe-clean: needs the reaction concept, not metabolism detail.
  biochemistry: {
    q: 'What does an enzyme do to a biochemical reaction?',
    opts: [
      'It supplies the energy that the reaction needs to occur',
      'It lowers the activation energy, speeding the reaction without being consumed',
      'It makes an otherwise impossible reaction thermodynamically favorable',
      'It is used up in the reaction and must be continually replaced'
    ],
    a: 1,
    concept: 'Enzymes are biological catalysts: they lower the activation-energy barrier so a reaction proceeds faster, are not consumed, and do NOT change whether the reaction is thermodynamically favorable (they don\u2019t shift equilibrium). This is the central mechanistic idea linking organic chemistry to life.',
    trap: 'Option 0 ("supplies energy") and option 2 ("makes it favorable") both confuse KINETICS (rate) with THERMODYNAMICS (whether it happens at all) \u2014 the deepest and most persistent biochemistry misconception. Option 3 (consumed) misses the defining catalytic property. Detecting these separates real mechanistic understanding from a vocabulary-level grasp.'
  },
};

// Convenience: attach items to registry node records.
// Usage in the diagnostic: node.question = CHEMISTRY_ITEMS[node.id] || null;
export function attachChemistryItems(nodes) {
  return nodes.map(n => (CHEMISTRY_ITEMS[n.id] ? { ...n, question: CHEMISTRY_ITEMS[n.id] } : n));
}
